/* Helper Functions */

import { CSSProperties, ElementType, ReactNode } from 'react'
import { Navigate, RouteProps } from 'react-router-dom'
import { ComplianceIntake } from '@/features/compliance/intake'
import { CompliancePositionRoute } from '@/features/compliance/CompliancePositionRoute'
import { ComplianceReport } from '@/features/compliance/report'
import { CompliancePlanPricing } from '@/features/compliance/plan-pricing'
import { COMPLIANCE_PATHS } from '@/features/compliance/lib/constants'
import { QualifiedOnlyRoute } from '@/features/compliance/QualifiedOnlyRoute'
import { MASS_MESSAGE_ADMIN } from '@/utils/adminHelpers'
import { User } from '@/reducers/auth/userReducer'
import type { Colors } from '../../styles/theme'

/**
 * ⚠️ Routes that need to be converted for DSM 2 ⚠️
 */

/* Accounts */
import FinancesNewAccounts from '@/componentsV1/Finances/Accounts/NewAccount'

/* Admin */
import { AdminFinancesPanel } from '@/features/admin'
import AdminListAllUsersPanel from '@/features/admin/paginated-users-table/ListAllUsersPanel'
import AllInternalUsersPanel from '@/features/admin/AllInternalUsersPanel'
import AdminAllBookkeepingReportsPanel from '@/features/admin/AllBookkeepingReportsPanel'
import { AdminAllocationGuidePanel } from '@/features/admin/AllocationGuidePanel'
import AdminIncompleteBookkeepingReportsPanel from '@/features/admin/IncompleteBookkeepingReportsPanel'
import AdminUserTransactionsPanel from '@/features/admin/UserTransactionPanel'
import TransactionCategoriesPanel from '@/features/admin/transaction-categories/TransactionCategoriesPanel'
import AdminTransactionRulesPanel from '@/features/admin/autocategorization/TransactionRulesPanel'
import AdminUserRecordPanel from '@/features/admin/user-record'
import AdminUserImportedTransactionsPanel from '@/features/admin/user-transactions-panels/ImportedTransactionsPanel'
import AdminMonthlyBooksPanel from '@/features/admin/user-transactions-panels/MonthlyBooksPanelSwitch'
import AllActionItemsPanel from '@/features/admin/action-items/AllActionItemsPanel'
import AdminUserDocumentCategoriesPanel from '@/features/admin/user-document-categories'
import AdminReconciliationPanel from '@/features/admin/reconciliation/Screens/ReconciliationPanel'
import BookkeepingCRM from '@/features/admin/bookkeeping-crm'
import QAPanel from '@/features/admin/qa'
import AnnualTaxesPanel from '@/features/admin/taxes'
import ZendeskBulkMessagePanel from '@/features/admin/zendesk-bulk-messages'
import { UserCallsPanel } from '@/features/admin/user-record/UserCallsPanel'
// TEMP CORE-9199 remove after Oct 30
import { TempShadowSessionPage } from '@/features/admin/temp-shadow-session/TempShadowSessionPage'
import AdminProfile from '@/features/admin/admin-profile'
import AnnualTaxFilingUserPanel from '@/features/admin/taxes/AnnualTaxFilingUserPanel'
import StateIncomeTaxDetailsPanel from '@/features/admin/taxes/income-tax-details/state-income-tax-details'
import FederalIncomeTaxDetailsPanel from '@/features/admin/taxes/income-tax-details/federal-income-tax-details'
import QuarterlyTaxCalculationsPanel from '@/features/admin/user-tax-calculations'
import { QuarterlyTaxEstimateDetailsPanel } from '@/features/admin/quarterly-tax-estimate-details'
import AnnualTaxDetailsPanel from '@/features/admin/annual-tax-details'
import EmailUsers from '@/features/admin/email-users'
import AdminBookkeepingEndOfYearReview from '@/features/bookkeeping/end-of-year-review/admin'

/* ------------------------------------------------------------------------- */
/*                          🎨   V2 Imports   🎨                             */
/* ------------------------------------------------------------------------- */

/* Login */
import { Login } from '@/features/login'
import { AdminLogin } from '@/features/admin-login'
import { ResetPassword } from '@/features/login/ResetPassword'
import { ResetPasswordForm } from '@/features/login/ResetPasswordForm'
import { AccountSuspended } from '@/features/accounts'
import { CanceledAccount } from '@/features/accounts/CanceledAccount'

/* Signup */
import { EmailCapture } from '@/features/user/signup/email-capture'
import { AboutYou } from '@/features/user/signup/about-you'
import { PracticeReadinessAccount } from '@/features/user/signup/practice-readiness/account'
import { YourPractice } from '@/features/user/signup/your-practice'
import { ChoosePlan } from '@/features/user/signup/choose-plan'
import { Payment } from '@/features/user/signup/payment'
import { DisqualifiedSignup } from '@/features/user/signup/disqualified'
import { Consult } from '@/features/user/signup/consult'
import { Success } from '@/features/user/signup/success'
import { SignupWaitlist } from '@/features/user/signup/your-practice/SignupWaitlist'
import { SIGNUP_PATHS } from '@/features/user/signup/helpers'
import { PreviousYearTaxForm } from '@/features/user/signup/previous-tax-year-form'

/* Insights */
import { ProfitAndLossPanel } from '@/features/reports/ProfitAndLossPanel'
import { CatchUpBookkeepingUnpaid } from '@/features/accounts/CatchUpBookkeepingUnpaid'

/* Settings */
import { SelfServiceSettings } from '@/features/user/settings/sections/self-service'
import { SelfServeCancellation } from '@/features/user/settings/sections/self-serve-cancellation'
import { PlanChange } from '@/features/user/settings/sections/plan-change'
import { PaymentMethodPage } from '@/features/user/settings/sections/plans-billing-card/PaymentMethodPage'
import { BillingInformationPage } from '@/features/user/settings/sections/plans-billing-card/BillingInformationPage'
import {
  BILLING_INFORMATION_PATH,
  PAYMENT_METHOD_PATH,
} from '@/features/user/settings/sections/plans-billing-card/paths'

/* Dashboard */
import { Dashboard } from '@/features/user/dashboard'
import { OnboardingDashboard } from '@/features/user/dashboard/onboarding'

/* Onboarding */
import { OnboardingV2 } from '@/features/onboarding'
import { SetupLanding } from '@/features/onboarding/setup-landing/SetupLanding'
import { SetupBankAccountPreScreenCard } from '@/features/onboarding/connect-stage/SetupBankAccountPreScreenCard'
import { ConnectBusinessAccounts } from '@/features/onboarding/connect-stage/ConnectBusinessAccounts'
import { SetupComplete } from '@/features/onboarding/SetupComplete'
import { BookkeepingFlow } from '@/features/onboarding/aha-moments/bookkeeping'
import { TaxesFlow } from '@/features/onboarding/aha-moments/taxes'

/* Your Practice */
/* Your Practice - Profile */
import { PracticeProfile } from '@/features/user/your-practice/profile'
import { PersonalInformationForm } from '@/features/user/your-practice/profile/PersonalInformationForm'
import { YourBusiness } from '@/features/user/your-practice/profile/your-business'
import { TaxInformationForm } from '@/features/user/your-practice/profile/tax-information/Form'

/* Your Practice - Documents */
import { Documents } from '@/features/user/your-practice/documents'

/* Your Practice - Statements */
import { StatementsUploadPage } from '@/features/user/your-practice/statements'

/* V2 PNL Reports */
import { AdminProfitAndLossPanel } from '@/features/reports/Admin/AdminProfitAndLossPanel'
import { AdminGeneralLedgerPanel } from '@/features/reports/Admin/AdminGeneralLedgerPanel'
import { AdminBalanceSheetPanel } from '@/features/reports/Admin/AdminBalanceSheet/AdminBalanceSheetPanel'
import { AdminAutoBalanceSheetPanel } from '@/features/reports/Admin/AdminBalanceSheet/AdminAutoBalanceSheetPanel'
import { AdminGeneralJournalPanel } from '@/features/reports/Admin/AdminGeneralJournalPanel'

/* Allocation Guides */
import { AllocationGuidePanel } from '@/features/allocation-guides'
import { AllocationCreation } from '@/features/allocation-guides/AllocationCreation'
import { SingleAllocationGuidePanel } from '@/features/allocation-guides/SingleAllocationGuidePanel'

/* Transactions */
import { TransactionsPanel } from '@/features/transactions'
import { UserTransactionsByCategory } from '@/features/financial-insights/transactions-panel/UserTransactionsByCategory'

/* Taxes Profile */
import { RecalculationProfileFlow } from '@/features/taxes/taxes-profile/recalculation-form-flow'
import { TaxesProfileFlow } from '@/features/taxes/taxes-profile/form-flow'
import { QuarterlyTaxEstimatesPanel } from '@/features/taxes/quarterly'

/* Annual Tax */
import { TaxReturnArchive } from '@/features/taxes/tax-return-archive'

/* Quarterly Tax Estimates */
import { EstimateBreakdown } from '@/features/taxes/quarterly/components/estimate-breakdown/EstimateBreakdown'
import { CalculateLateHarborFlow } from '@/features/taxes/quarterly/components/calculate-safe-harbor-flow'
import { EstimateBreakdownSample } from '@/features/taxes/quarterly/components/estimate-breakdown/EstimateBreakdownSample'
import {
  BasicPlanQTEUpsell,
  BasicPlanQTEUpsellBanner,
} from '@/features/taxes/quarterly/BasicPlanUpsell'

/* Financial Insights */
import { InsightsExpensesPanel } from '@/features/financial-insights/expenses'
import { InsightsIncomePanel } from '@/features/financial-insights/income'
import { InsightsProfitPanel } from '@/features/financial-insights/profit'
import { BookkeepingReportPanel } from '@/features/financial-insights/reports/bookkeeping-reports'
import { EoyAlertBanner } from '@/features/financial-insights/reports/bookkeeping-reports/EoyAlertBanner'
import { YearlyReportsPanel } from '@/features/financial-insights/reports/YearlyReportsPanel'
import { PracticeOverview } from '@/features/financial-insights/practice-insights'
import { ExpensesPanel } from '@/features/financial-insights/expenses-breakdown'
import { BookkeepingReportReroute } from '@/features/financial-insights/reports/BookkeepingReportReroute'

/* Financial Advisory */
import { FinancialAdvisoryOverview } from '@/features/financial-advisory'
import { FinancialAdvisoryGoals } from '@/features/financial-advisory/panels/GoalsPanel'
import { FinancialAdvisoryInbox } from '@/features/financial-advisory/panels/InboxPanel'

/* Other */
import ZendeskSso from '@/features/zendesk/ZendeskSso'
import { MaintenanceBanner } from '@/components/platform-banners/MaintenanceBanner'
import { ProductFeatureId } from '@/actions/permissionActions'
import { RedirectToExternal } from '@/components/routes/RedirectToExternal'
import { NotFound } from '@/components/routes/NotFound'
import { GoalSetting } from '@/features/goal-setting'
import { IncomePlanningMonths } from '@/features/income-planning/income-planning-months'
import { CreateEditIncomePlan } from '@/features/income-planning/plan-flows/CreateEditIncomePlan'
import { IncomePlanningUpdateGoal } from '@/features/income-planning/update-goal'

/* Payroll */
import { PayrollUpsell } from '@/features/payroll/upsell'
import { GEP_ENROLL_PATHS } from '@/features/payroll/helpers'
import { PayrollInterestFlow } from '@/features/payroll/upsell/interest-flow/PayrollInterestFlow'
import { PayrollEmployees } from '@/features/payroll/employees'
import { PayrollSettings } from '@/features/payroll/settings'
import { PayrollEmployee } from '@/features/payroll/employee'
import { PayrollContractor } from '@/features/payroll/contractor'
import { RunPayroll } from '@/features/payroll/run-payroll'
import { PayrollHistory } from '@/features/payroll/history'
import { PayrollEnroll } from '@/features/payroll/enroll'
import { CreateTeamMember } from '@/features/payroll/create-team-member/index'
import { CreateEmployee } from '@/features/payroll/create-team-member/CreateEmployee'
import { CreateContractor } from '@/features/payroll/create-team-member/CreateContractor'
import { MigratePayroll } from '@/features/payroll/migrate'
import { ProcessMigration } from '@/features/payroll/migrate/ProcessMigration'
import { PayrollHistoryDetails } from '@/features/payroll/history/PayrollHistoryDetails'
import { ProcessPayroll } from '@/features/payroll/run-payroll/employee-payroll/ProcessPayroll'
import { OffCyclePayroll } from '@/features/payroll/run-payroll/employee-payroll/OffCyclePayroll'
import { PayrollDocuments } from '@/features/payroll/payroll-documents/index'
import { PayrollOverview } from '@/features/payroll/enroll/PayrollOverview'
import { PayrollCompanySetup } from '@/features/payroll/enroll/PayrollCompanySetup'
import { PayrollEnrollLocations } from '@/features/payroll/enroll/PayrollEnrollLocations'
import { PayrollEnrollEmployee } from '@/features/payroll/enroll/PayrollEnrollEmployee'
import { PayrollEnrollSchedule } from '@/features/payroll/enroll/PayrollEnrollSchedule'
import { PayrollEnrollFederal } from '@/features/payroll/enroll/PayrollEnrollFederal'
import { PayrollEmployeeState } from '@/features/payroll/enroll/PayrollEmployeeState'
import { PayrollEnrollBank } from '@/features/payroll/enroll/PayrollEmployeeBank'
import { PayrollEnrollGustoFlow } from '@/features/payroll/enroll/PayrollEnrollGustoFlow'
import { PayrollEnrollComplete } from '@/features/payroll/enroll/PayrollEnrollComplete'
import { ContractorPayroll } from '@/features/payroll/run-payroll/contractor-payroll/ContractorPayroll'
import { PayrollHistoryContractorDetails } from '@/features/payroll/history/PayrollContractorHistoryDetails'
import { CreateEmployeeJob } from '@/features/payroll/create-team-member/CreateEmployeeJob'
import { PayrollEmployeeFederal } from '@/features/payroll/enroll/PayrollEmployeeFederal'
import { PayrollEnrollEmployeeJob } from '@/features/payroll/enroll/PayrollEnrollEmployeeJob'
import { PayrollUsers } from '@/features/payroll/payroll-users'

/* Annual Tax */
import { AnnualTaxesOverviewPanel } from '@/features/taxes/annual-taxes/overview'
import TaxChecklistDocuments from '@/features/taxes/annual-taxes/tax-checklist/documents'
import LifeChanges from '@/features/taxes/annual-taxes/tax-checklist/form1040/life-changes'
import TaxQuestionnaire1120S from '@/features/taxes/annual-taxes/tax-checklist/form1120S'
import AdminTaxChecklist from '@/features/taxes/annual-taxes/tax-checklist/admin'
import TaxChecklist from '@/features/taxes/annual-taxes/tax-checklist/TaxChecklist'
import TaxfyleShortForm from '@/features/taxes/taxfyle-short-form/TaxfyleShortForm'
import Form1040Details from '@/features/taxes/annual-taxes/tax-checklist/form1040/details'
import AddMissingQTEPayments from '@/features/taxes/annual-taxes/tax-checklist/form1040/add-missing-qte-payments/AddMissingQTEPayments'
import DeductionsAndCredits from '@/features/taxes/annual-taxes/tax-checklist/form1040/deductions-and-credits'
import IncomeAndLiabilities from '@/features/taxes/annual-taxes/tax-checklist/form1040/income-and-liabilities'
import {
  CONTRACTOR_FILING_QUESTIONNAIRE_PATH,
  TaxChecklistPaths,
} from '@/features/taxes/annual-taxes/constants'
import ExtensionSurveyFormBusiness from '@/features/taxes/annual-taxes/extension-survey/business'
import ExtensionSurveyFormPersonal from '@/features/taxes/annual-taxes/extension-survey/personal'
import ExtensionSurveyFormCombined from '@/features/taxes/annual-taxes/extension-survey/combined'
import YearEndKickoffSurvey from '@/features/taxes/annual-taxes/tax-season-kickoff/YearEndKickoffSurvey'
import RequestExtension from '@/features/taxes/annual-taxes/extension-survey/RequestExtension'
import ExtensionSurveyReadOnly from '@/features/taxes/annual-taxes/extension-survey/ExtensionSurveyReadOnly'
import TaxOptIn from '@/features/taxes/annual-taxes/tax-opt-in/TaxOptIn'
import ContractorFilingQuestionnairePage from '@/features/taxes/annual-taxes/ten-99-filing/ContractorFilingQuestionnairePage'
import { DeadlineBanner } from '@/features/taxes/annual-taxes/tax-season-kickoff/DeadlineBanners'

import TaxAdvisoryRequest from '@/features/taxes/advisory/TaxConsultationRequest'
import TaxAdvisoryPaymentConfirmation from '@/features/taxes/advisory/TaxConsultationPaymentConfirmation'

/* YE Bookkeeping */
import { FinalReviewIntro } from '@/features/bookkeeping/final-review/final-review-intro'
import { UnclarifiedTransactions } from '@/features/bookkeeping/review-and-close-books/follow-up-on-transactions'
import { FollowUpOnStatements } from '@/features/bookkeeping/review-and-close-books/follow-up-on-statements'
import { FollowUpOnDocuments } from '@/features/bookkeeping/review-and-close-books/follow-up-on-documents'
import { FinalSignOff } from '@/features/bookkeeping/final-review/final-sign-off'
import FinalReviewIncome from '@/features/bookkeeping/final-review/final-review-income'
import FinalReviewExpenses from '@/features/bookkeeping/final-review/final-review-expenses'
import { EndOfYearReviewContainer } from '@/features/bookkeeping/end-of-year-review'
import { ReviewAndCloseBooks } from '@/features/bookkeeping/review-and-close-books'
import { Banking } from '@/features/banking'
import {
  BankingIntroDashboardRoute,
  BankingApplicationDashboardRoute,
  ConnectDashboardRoute,
} from '@/features/banking/banking-dashboard-routes'
import { FlaggedComponent } from '@/components/FlaggedComponent'
import { FEATURE_FLAG_KEYS } from '@/flags'
import { Loader } from '@/ui'
import Referrals from '@/features/user/referrals'
import { HavenRoute } from '@/features/adviser-chat/components/HavenRoute'
import {
  HAVEN_CONVERSATION_PATH,
  HAVEN_PATH,
} from '@/features/adviser-chat/lib/havenRoutes'

/* ------------------------------------------------------------------------- */
/*                          🙏   Routes Below   🙏                            */
/* ------------------------------------------------------------------------- */

export type RouteConfig = {
  [key: string]: Omit<RouteProps, 'element' | 'path' | 'index' | 'lazy'> & {
    component: ElementType
    customAuth?: (user: User) => boolean
    banner?: ElementType | ElementType[]
    backgroundColor?: keyof typeof Colors
    innerContentWrapperStyle?: CSSProperties
    contentWrapperStyle?: CSSProperties
    /** Restricts access to the route based on the user's permissions
     * @param features - The features that the user must have access to in order to access the route
     * @param includeChildRoutes - If true, the permissions will be checked for all child routes (routes that start with the same path)
     * e.g. setting to true for /taxes/annual would block any route that starts with /taxes/annual/
     */
    requiredPermissions?: {
      features: ProductFeatureId[]
      includeChildRoutes?: boolean
      customPermissionComponent?: ReactNode
    }
  }
}

export const EXTERNAL_ADMIN_ROUTES: RouteConfig = {
  '/admin/finances/list-users': {
    component: AdminListAllUsersPanel,
  },
  '/admin/profile': {
    component: AdminProfile,
  },
  '/admin/finances': {
    component: AdminFinancesPanel,
  },
  '/admin/finances/annual-tax-filings': {
    component: AnnualTaxesPanel,
    backgroundColor: 'white',
  },
  '/admin/finances/annual-tax-filings/:filingId': {
    component: AnnualTaxFilingUserPanel,
  },
  '/admin/finances/transactions/:userId': {
    component: AdminUserTransactionsPanel,
  },
  '/admin/finances/records/:userId/monthly-books/:reportId': {
    component: AdminMonthlyBooksPanel,
  },
  '/admin/finances/financial-reports/:userId/profit-loss': {
    component: AdminProfitAndLossPanel,
  },
  '/admin/finances/financial-reports/:userId/general-ledger': {
    component: AdminGeneralLedgerPanel,
  },
  '/admin/finances/financial-reports/:userId/general-journal': {
    component: AdminGeneralJournalPanel,
  },
  '/admin/finances/financial-reports/:userId/balance-sheet': {
    component: AdminBalanceSheetPanel,
  },
  '/admin/finances/financial-reports/:userId/auto-balance-sheet': {
    component: AdminAutoBalanceSheetPanel,
  },
  '/admin/finances/transactions/:userId/imported': {
    component: AdminUserImportedTransactionsPanel,
  },
  '/admin/finances/records/:userId': {
    component: AdminUserRecordPanel,
  },
  '/admin/finances/records/:userId/calls': {
    component: UserCallsPanel,
  },
  // TEMP CORE-9199 remove after Oct 30
  '/admin/finances/records/:userId/shadow': {
    component: TempShadowSessionPage,
  },
  '/admin/finances/records/:userId/allocation-guides/:allocationGuideId': {
    component: AdminAllocationGuidePanel,
  },
  '/admin/finances/records/:userId/submissions/:year': {
    component: AdminBookkeepingEndOfYearReview,
  },
}

export const ADMIN_ROUTES: RouteConfig = {
  '/admin/finances/all-internal-users': {
    component: AllInternalUsersPanel,
  },
  '/admin/finances/mass-messaging': {
    customAuth: (user: User) => MASS_MESSAGE_ADMIN(user),
    component: ZendeskBulkMessagePanel,
  },
  '/admin/finances/email-users': {
    component: EmailUsers,
  },
  '/admin/finances/books/crm': {
    component: BookkeepingCRM,
  },
  '/admin/finances/books': {
    component: AdminAllBookkeepingReportsPanel,
  },
  '/admin/finances/books/incomplete': {
    component: AdminIncompleteBookkeepingReportsPanel,
  },
  '/admin/finances/transaction-categories': {
    component: TransactionCategoriesPanel,
  },
  '/admin/finances/transaction-rules': {
    component: AdminTransactionRulesPanel,
  },

  '/admin/finances/all-action-items': {
    component: AllActionItemsPanel,
  },
  '/admin/finances/user-document-categories': {
    component: AdminUserDocumentCategoriesPanel,
  },
  '/admin/finances/quarterly-tax-estimate-details': {
    component: QuarterlyTaxEstimateDetailsPanel,
  },
  '/admin/finances/annual-tax-details': {
    component: AnnualTaxDetailsPanel,
  },
  '/admin/finances/qte-calculations': {
    component: QuarterlyTaxCalculationsPanel,
  },
  '/admin/finances/federal-income-tax': {
    component: FederalIncomeTaxDetailsPanel,
  },
  '/admin/finances/state-income-tax': {
    component: StateIncomeTaxDetailsPanel,
  },
  '/admin/finances/tax-checklist': {
    component: AdminTaxChecklist,
  },
  '/admin/finances/records/:userId/monthly-books/:reportId/reconciliation/:reconciliationId':
    {
      component: AdminReconciliationPanel,
    },
  '/admin/finances/qa': {
    component: QAPanel,
  },
  '/admin/payroll/users': {
    component: PayrollUsers,
  },
}

export const ANNUAL_TAX_CENTER_ROUTE_CONFIG = {
  component: AnnualTaxesOverviewPanel,
  backgroundColor: 'white' as const,
  requiredPermissions: {
    features: [ProductFeatureId.annualTaxes],
    includeChildRoutes: true,
    // When a user doesn't have annual tax permission, force them to see the tax overview panel.
    // This panel has built-in logic on what to display to users without permissions.
    // Note: it is not always the upsell view (e.g. when tax season is over, it shows the next-year screen).
    customPermissionComponent: <AnnualTaxesOverviewPanel />,
  },
}

export const PRIVATE_ROUTES_ANNUAL_TAXES: RouteConfig = {
  '/taxes/annual': ANNUAL_TAX_CENTER_ROUTE_CONFIG,
  '/taxes/annual/complete-year-end-bookkeeping': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/intro': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/review-disconnected-accounts':
    {
      component: EndOfYearReviewContainer,
      backgroundColor: 'white',
    },
  '/taxes/annual/complete-year-end-bookkeeping/:year/upload-missing-bank-statements':
    {
      component: EndOfYearReviewContainer,
      backgroundColor: 'white',
    },
  '/taxes/annual/complete-year-end-bookkeeping/:year/categorize-transactions': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/other-income': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/other-expenses': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/business-income-in-personal':
    {
      component: EndOfYearReviewContainer,
      backgroundColor: 'white',
    },
  '/taxes/annual/complete-year-end-bookkeeping/:year/business-expenses-in-personal':
    {
      component: EndOfYearReviewContainer,
      backgroundColor: 'white',
    },
  '/taxes/annual/complete-year-end-bookkeeping/:year/review-owners-investments':
    {
      component: EndOfYearReviewContainer,
      backgroundColor: 'white',
    },
  '/taxes/annual/complete-year-end-bookkeeping/:year/review-owners-distributions':
    {
      component: EndOfYearReviewContainer,
      backgroundColor: 'white',
    },
  '/taxes/annual/complete-year-end-bookkeeping/:year/upload-documents-intro': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/payroll': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/contractor-payments': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/processing-fees': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/first-submission': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/all-done': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/review-income': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/review-expenses': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/fixed-asset-depreciation':
    {
      component: EndOfYearReviewContainer,
      backgroundColor: 'white',
    },
  '/taxes/annual/complete-year-end-bookkeeping/:year/previous-tax-return': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/complete-year-end-bookkeeping/:year/review-document-uploads': {
    component: EndOfYearReviewContainer,
    backgroundColor: 'white',
  },
  '/taxes/annual/review-and-close-books/:year/follow-up': {
    component: ReviewAndCloseBooks,
    backgroundColor: 'white',
  },
  '/taxes/annual/review-and-close-books/:year/final-review-intro': {
    component: FinalReviewIntro,
    backgroundColor: 'white',
  },
  '/taxes/annual/review-and-close-books/:year/final-review-income': {
    component: FinalReviewIncome,
    backgroundColor: 'white',
  },
  '/taxes/annual/review-and-close-books/:year/final-review-expenses': {
    component: FinalReviewExpenses,
    backgroundColor: 'white',
  },
  '/taxes/annual/review-and-close-books/:year/final-sign-off': {
    component: FinalSignOff,
    backgroundColor: 'white',
  },
  '/taxes/annual/review-and-close-books/:year/follow-up-transactions': {
    component: UnclarifiedTransactions,
    backgroundColor: 'white',
  },
  '/taxes/annual/review-and-close-books/:year/follow-up-statements': {
    component: FollowUpOnStatements,
    backgroundColor: 'white',
  },
  '/taxes/annual/review-and-close-books/:year/follow-up-documents': {
    component: FollowUpOnDocuments,
    backgroundColor: 'white',
  },

  '/taxes/annual/kickoff-survey': {
    component: YearEndKickoffSurvey,
    backgroundColor: 'white',
  },
  '/taxes/annual/tax_checklist/': {
    component: TaxChecklist,
    backgroundColor: 'white',
  },
  '/taxes/annual/short_form': {
    component: TaxfyleShortForm,
    backgroundColor: 'white',
  },
  [`/taxes/annual/tax_checklist/:formId/${TaxChecklistPaths.addMissingQtePayments}`]:
    {
      component: AddMissingQTEPayments,
      backgroundColor: 'white',
    },
  [`/taxes/annual/tax_checklist/:formId/${TaxChecklistPaths.lifeChanges}`]: {
    component: LifeChanges,
    backgroundColor: 'white',
  },
  [`/taxes/annual/tax_checklist/:formId/${TaxChecklistPaths.deductionsAndCredits}`]:
    {
      component: DeductionsAndCredits,
      backgroundColor: 'white',
    },
  [`/taxes/annual/tax_checklist/:formId/${TaxChecklistPaths.incomeAndLiabilities}`]:
    {
      component: IncomeAndLiabilities,
      backgroundColor: 'white',
    },
  [`/taxes/annual/tax_checklist/:formId/${TaxChecklistPaths.checkYourDetails}`]:
    {
      component: TaxQuestionnaire1120S,
      backgroundColor: 'white',
    },
  [`/taxes/annual/tax_checklist/:formId/${TaxChecklistPaths.checkYourDetailsPersonal}`]:
    {
      component: Form1040Details,
      backgroundColor: 'white',
    },
  [`/taxes/annual/tax_checklist/:formId/${TaxChecklistPaths.documents}`]: {
    component: TaxChecklistDocuments,
    backgroundColor: 'white',
  },
  '/taxes/annual/tax_consultations': {
    component: TaxAdvisoryRequest,
    backgroundColor: 'white',
  },
  '/taxes/annual/tax_consultations/:taxConsultationRequestId/confirmed': {
    component: TaxAdvisoryPaymentConfirmation,
    backgroundColor: 'white',
  },
  '/taxes/annual/extension_request': {
    component: RequestExtension,
    backgroundColor: 'white',
  },
  [CONTRACTOR_FILING_QUESTIONNAIRE_PATH]: {
    component: ContractorFilingQuestionnairePage,
    backgroundColor: 'white',
  },
  '/taxes/annual/extension_request/business': {
    component: ExtensionSurveyFormBusiness,
    backgroundColor: 'white',
  },
  '/taxes/annual/extension_request/personal': {
    component: ExtensionSurveyFormPersonal,
    backgroundColor: 'white',
  },
  '/taxes/annual/extension_request/combined': {
    component: ExtensionSurveyFormCombined,
    backgroundColor: 'white',
  },
  '/taxes/annual/extension_request/complete/:taxYear': {
    component: ExtensionSurveyReadOnly,
    backgroundColor: 'white',
  },
  '/taxes/annual/opt-in': {
    component: TaxOptIn,
    backgroundColor: 'white',
  },
}

export const PRIVATE_ROUTES: RouteConfig = {
  '/': {
    component: Dashboard,
    banner: [MaintenanceBanner, DeadlineBanner],
    backgroundColor: 'white',
  },
  '/dashboard': {
    component: Dashboard,
    banner: [MaintenanceBanner, DeadlineBanner],
    backgroundColor: 'white',
  },
  [HAVEN_PATH]: {
    component: HavenRoute,
  },
  [HAVEN_CONVERSATION_PATH]: {
    component: HavenRoute,
  },
  '/onboarding-dashboard': {
    component: OnboardingDashboard,
    banner: [MaintenanceBanner, DeadlineBanner],
    backgroundColor: 'white',
    innerContentWrapperStyle: {
      padding: 0,
    },
  },
  '/onboarding': {
    component: OnboardingV2,
    backgroundColor: 'white',
  },
  '/onboarding-v2': {
    component: OnboardingV2,
    backgroundColor: 'white',
  },
  '/onboarding-v2/complete': {
    component: SetupComplete,
    backgroundColor: 'white',
  },
  '/onboarding-v2/learning/bookkeeping': {
    component: BookkeepingFlow,
    backgroundColor: 'white',
  },
  '/onboarding-v2/learning/taxes': {
    component: TaxesFlow,
    backgroundColor: 'white',
  },
  '/onboarding-v2/estimate-breakdown-sample': {
    component: EstimateBreakdownSample,
    backgroundColor: 'white',
  },
  '/onboarding/setup/landing': {
    component: SetupLanding,
    backgroundColor: 'white',
  },
  '/onboarding/setup/prescreen': {
    component: SetupBankAccountPreScreenCard,
    backgroundColor: 'white',
  },
  '/onboarding/setup/connect-bank': {
    component: ConnectBusinessAccounts,
    backgroundColor: 'white',
  },

  // Settings
  '/accounts': {
    component: SelfServiceSettings,
  },
  /* Unclear if we need this */
  '/accounts/new': {
    component: FinancesNewAccounts,
  },
  /* Unclear if we need this */
  '/accounts/new/oauth': {
    component: FinancesNewAccounts,
  },
  '/accounts/verify/:token': {
    component: SelfServiceSettings,
  },
  '/accounts/choose-plan': {
    component: () => <Payment isUpsell />,
  },
  '/accounts/suspended': {
    component: AccountSuspended,
  },
  '/accounts/canceled': {
    component: CanceledAccount,
  },
  '/accounts/zendesk/sso': {
    component: ZendeskSso,
    backgroundColor: 'white',
  },
  '/plan-change': {
    component: PlanChange,
  },
  [PAYMENT_METHOD_PATH]: {
    component: PaymentMethodPage,
  },
  [BILLING_INFORMATION_PATH]: {
    component: BillingInformationPage,
  },

  '/transactions': {
    component: TransactionsPanel,
    backgroundColor: 'white',
  },
  '/transactions/:type': {
    component: TransactionsPanel,
    backgroundColor: 'white',
  },
  '/transactions/expenses/:transactionCategoryId': {
    component: UserTransactionsByCategory,
  },

  '/reports/bookkeeping/:reportName': {
    component: BookkeepingReportPanel,
    banner: EoyAlertBanner,
    backgroundColor: 'white',
  },
  '/reports/bookkeeping/:bookkeepingReportMonth/:bookkeepingReportYear/:reportName':
    {
      component: BookkeepingReportPanel,
      banner: EoyAlertBanner,
      backgroundColor: 'white',
    },
  '/reports/bookkeeping/:bookkeepingReportId/:reportName': {
    component: BookkeepingReportReroute,
    backgroundColor: 'white',
  },
  '/reports/yearly': {
    component: YearlyReportsPanel,
    backgroundColor: 'white',
  },
  '/reports/profit-loss': {
    component: ProfitAndLossPanel,
    backgroundColor: 'white',
  },
  '/trends/expenses': {
    component: ExpensesPanel,
    backgroundColor: 'white',
  },
  '/insights/overview': {
    component: PracticeOverview,
    backgroundColor: 'white',
  },
  '/insights/expenses': {
    component: InsightsExpensesPanel,
    backgroundColor: 'white',
  },
  '/insights/income': {
    component: InsightsIncomePanel,
    backgroundColor: 'white',
  },
  '/insights/profit': {
    component: InsightsProfitPanel,
    backgroundColor: 'white',
  },

  '/taxes/archive': {
    component: TaxReturnArchive,
    backgroundColor: 'white',
  },
  '/taxes': {
    component: () => <Navigate replace to="/taxes/annual" />,
  },
  '/taxes/quarterly': {
    component: QuarterlyTaxEstimatesPanel,
    banner: [BasicPlanQTEUpsellBanner],
    backgroundColor: 'white',
    requiredPermissions: {
      features: [ProductFeatureId.quarterlyTaxes],
      includeChildRoutes: true,
      customPermissionComponent: <BasicPlanQTEUpsell />,
    },
  },

  '/taxes/quarterly/recalculate': {
    component: RecalculationProfileFlow,
    backgroundColor: 'white',
  },
  '/taxes/quarterly/calculate-safe-harbor': {
    component: CalculateLateHarborFlow,
    backgroundColor: 'white',
  },
  '/taxes/quarterly/:taxYear': {
    component: QuarterlyTaxEstimatesPanel,
  },
  '/taxes/quarterly/:taxQuarter/estimate-breakdown': {
    component: EstimateBreakdown,
    backgroundColor: 'white',
  },
  '/taxes/quarterly/:taxYear/:taxQuarter': {
    component: QuarterlyTaxEstimatesPanel,
  },

  /** PRACTICE - PROFILE **/
  '/practice/profile': {
    component: PracticeProfile,
    backgroundColor: 'white',
  },
  '/practice/profile/editBusiness': {
    component: YourBusiness,
    backgroundColor: 'white',
  },
  // Backwards compat link
  '/practice/profile/:profileId/edit': {
    component: () => <Navigate replace to="/practice/profile/editBusiness" />,
  },
  // Backwards compat link
  '/practice/profile/edit': {
    component: YourBusiness,
    backgroundColor: 'white',
  },
  '/practice/profile/editTax': {
    component: TaxInformationForm,
    backgroundColor: 'white',
  },
  '/practice/profile/editPersonal': {
    component: PersonalInformationForm,
    backgroundColor: 'white',
  },

  /** PRACTICE - DOCUMENTS **/
  '/practice/documents': {
    component: Documents,
    backgroundColor: 'white',
  },
  '/practice/documents/:tabName/:action': {
    component: Documents,
    backgroundColor: 'white',
  },

  '/practice/statements': {
    component: StatementsUploadPage,
    backgroundColor: 'white',
  },

  '/messages': {
    component: () => (
      <RedirectToExternal
        to={`https://${process.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com/hc/en-us/requests?utf8=%E2%9C%93&query=&status=answered`}
      />
    ),
  },
  '/conversations': {
    component: () => (
      <RedirectToExternal
        to={`https://${process.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com/hc/en-us/requests?utf8=%E2%9C%93&query=&status=answered`}
      />
    ),
    backgroundColor: 'white',
  },
  '/conversations/:id': {
    component: () => (
      <RedirectToExternal
        to={`https://${process.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com/hc/en-us/requests?utf8=%E2%9C%93&query=&status=answered`}
      />
    ),
    backgroundColor: 'white',
    innerContentWrapperStyle: {
      padding: 0,
    },
  },
  '/allocation-guides': {
    component: AllocationGuidePanel,
    backgroundColor: 'white',
  },
  '/allocation-guides/view/:allocationGuideId': {
    component: SingleAllocationGuidePanel,
    backgroundColor: 'white',
  },
  '/allocation-guides/:state': {
    component: AllocationCreation,
    backgroundColor: 'white',
  },
  '/taxes/profile': {
    component: TaxesProfileFlow,
  },
  '/user-cancellation': {
    component: SelfServeCancellation,
    backgroundColor: 'white',
  },
  '/rewards': {
    component: () => (
      <FlaggedComponent
        flagKey={FEATURE_FLAG_KEYS.referralExperienceEnabled}
        loadingComponent={<Loader />}
        trueComponent={<Referrals />}
        falseComponent={<Navigate replace to="/dashboard" />}
      />
    ),
  },
  '/payroll/interest': {
    component: PayrollInterestFlow,
    backgroundColor: 'white',
  },
  '/payroll/welcome': {
    component: PayrollUpsell,
    backgroundColor: 'white',
  },
  [COMPLIANCE_PATHS.intake]: {
    component: () => (
      <CompliancePositionRoute screen={COMPLIANCE_PATHS.intake}>
        <ComplianceIntake />
      </CompliancePositionRoute>
    ),
    backgroundColor: 'white',
  },
  [COMPLIANCE_PATHS.report]: {
    component: () => (
      <CompliancePositionRoute screen={COMPLIANCE_PATHS.report}>
        <ComplianceReport />
      </CompliancePositionRoute>
    ),
    backgroundColor: 'white',
  },
  [COMPLIANCE_PATHS.planPricing]: {
    component: () => (
      <CompliancePositionRoute screen={COMPLIANCE_PATHS.planPricing}>
        <QualifiedOnlyRoute>
          <CompliancePlanPricing />
        </QualifiedOnlyRoute>
      </CompliancePositionRoute>
    ),
    backgroundColor: 'white',
  },
  '/advisory/overview': {
    component: FinancialAdvisoryOverview,
    backgroundColor: 'white',
  },
  '/advisory/enroll': {
    component: FinancialAdvisoryOverview,
    backgroundColor: 'white',
  },
  '/advisory/goals': {
    component: FinancialAdvisoryGoals,
    backgroundColor: 'white',
  },
  '/advisory/messages': {
    component: FinancialAdvisoryInbox,
    backgroundColor: 'white',
  },
  '/accounts/suspended/catch-up-bookkeeping': {
    component: CatchUpBookkeepingUnpaid,
    backgroundColor: 'white',
  },
  '/goal-setting': {
    component: GoalSetting,
    backgroundColor: 'white',
  },
  '/income-planning': {
    component: IncomePlanningMonths,
    backgroundColor: 'white',
  },
  '/income-planning/create-plan': {
    component: CreateEditIncomePlan,
    backgroundColor: 'white',
  },
  '/income-planning/edit-plan/:id': {
    component: CreateEditIncomePlan,
    backgroundColor: 'white',
  },
  '/income-planning/update-goal': {
    component: IncomePlanningUpdateGoal,
    backgroundColor: 'white',
  },
  '/banking/intro': {
    component: BankingIntroDashboardRoute,
    backgroundColor: 'white',
  },
  '/banking/apply': {
    component: BankingApplicationDashboardRoute,
    backgroundColor: 'white',
  },
  '/accounts/connect': {
    component: ConnectDashboardRoute,
    backgroundColor: 'white',
  },
  '/banking': {
    component: Banking,
    backgroundColor: 'white',
  },
  '*': {
    component: NotFound,
  },
}

export const PAYROLL_ROUTES: RouteConfig = {
  [GEP_ENROLL_PATHS.intro]: {
    component: PayrollEnroll,
    backgroundColor: 'white',
  },
  [GEP_ENROLL_PATHS.overview]: {
    component: PayrollOverview,
  },
  [GEP_ENROLL_PATHS.company]: {
    component: PayrollCompanySetup,
  },
  [GEP_ENROLL_PATHS.federal]: {
    component: PayrollEnrollFederal,
  },
  [GEP_ENROLL_PATHS.locations]: {
    component: PayrollEnrollLocations,
  },
  [GEP_ENROLL_PATHS.employee]: {
    component: PayrollEnrollEmployee,
  },
  [GEP_ENROLL_PATHS.employeeJob]: {
    component: PayrollEnrollEmployeeJob,
  },
  [GEP_ENROLL_PATHS.employeeFederal]: {
    component: PayrollEmployeeFederal,
  },
  [GEP_ENROLL_PATHS.employeeState]: {
    component: PayrollEmployeeState,
  },
  [GEP_ENROLL_PATHS.employeeState]: {
    component: PayrollEmployeeState,
  },
  [GEP_ENROLL_PATHS.employeeBank]: {
    component: PayrollEnrollBank,
  },
  [GEP_ENROLL_PATHS.schedule]: {
    component: PayrollEnrollSchedule,
  },
  [GEP_ENROLL_PATHS.flow]: {
    component: PayrollEnrollGustoFlow,
  },
  [GEP_ENROLL_PATHS.complete]: {
    component: PayrollEnrollComplete,
  },
  '/payroll/migrate': {
    component: MigratePayroll,
    backgroundColor: 'white',
  },
  '/payroll/process_migration': {
    component: ProcessMigration,
  },
  '/payroll/settings': {
    component: PayrollSettings,
  },
  '/payroll/employees': {
    component: PayrollEmployees,
  },
  '/payroll/employee/:uuid': {
    component: PayrollEmployee,
  },
  '/payroll/create_team_member': {
    component: CreateTeamMember,
  },
  '/payroll/create_employee': {
    component: CreateEmployee,
  },
  '/payroll/create_employee/job/:uuid': {
    component: CreateEmployeeJob,
  },
  '/payroll/create_contractor': {
    component: CreateContractor,
  },
  '/payroll/contractor/:uuid': {
    component: PayrollContractor,
  },
  '/payroll/run_payroll': {
    component: RunPayroll,
  },
  '/payroll/run_payroll/process/:uuid': {
    component: ProcessPayroll,
  },
  '/payroll/run_contractor_payroll': {
    component: ContractorPayroll,
  },
  '/payroll/create_off_cycle_payroll': {
    component: OffCyclePayroll,
  },
  '/payroll/history': {
    component: PayrollHistory,
  },
  '/payroll/history/:uuid': {
    component: PayrollHistoryDetails,
  },
  '/payroll/contractor_history/:uuid': {
    component: PayrollHistoryContractorDetails,
  },
  '/payroll/documents': {
    component: PayrollDocuments,
  },
}

export const PUBLIC_ROUTES: RouteConfig = {
  '/recover': {
    component: ResetPassword,
  },
  '/reset/:token': {
    component: ResetPasswordForm,
  },
  '/login': {
    component: Login,
  },
  '/admin-login': {
    component: AdminLogin,
  },
  '/zendesk/sso': {
    component: Login,
  },
  [SIGNUP_PATHS.emailCapture]: {
    component: EmailCapture,
  },
  [SIGNUP_PATHS.referred]: {
    component: AboutYou,
  },
  // Kept out of SIGNUP_STEP_MAP: this flow has its own progress indicator.
  [SIGNUP_PATHS.readinessAccount]: {
    component: PracticeReadinessAccount,
  },
  // STEP 1
  // Title:  About You
  // Path:  /signup
  // Component: AboutYou
  [SIGNUP_PATHS.aboutYou]: {
    component: AboutYou,
  },
  // STEP 2
  // Title:  Your Practice
  // Path:  /signup/about-your-practice
  // Component: AboutYourPractice
  [SIGNUP_PATHS.aboutYourPractice]: {
    component: YourPractice,
  },
  // STEP 3
  // Title:  Choose Plan
  // Path:  /signup/practice-journey -- path here should be named /choose-plan - confusign
  // Component: ChoosePlan
  // TODO: will update paths to match step in upcoming rework
  [SIGNUP_PATHS.choosePlan]: {
    component: ChoosePlan,
  },
  // STEP 4
  // Title:  Payment
  // Path:  /signup/choose-plan  -- path here is the name of the previous step, this is confusing, should be /payment
  // Component: Payment
  // TODO: will update paths to match step in upcoming rework
  [SIGNUP_PATHS.payment]: {
    component: Payment,
  },
  [SIGNUP_PATHS.disqualified]: {
    component: DisqualifiedSignup,
  },
  [SIGNUP_PATHS.waitlist]: {
    component: SignupWaitlist,
  },
  [SIGNUP_PATHS.consult]: {
    component: Consult,
  },
  [SIGNUP_PATHS.previousYearTax]: {
    component: PreviousYearTaxForm,
  },
  [SIGNUP_PATHS.success]: {
    component: Success,
  },
}

export const ALL_ROUTES = {
  ...EXTERNAL_ADMIN_ROUTES,
  ...ADMIN_ROUTES,
  ...PAYROLL_ROUTES,
  ...PUBLIC_ROUTES,
  ...PRIVATE_ROUTES,
  ...PRIVATE_ROUTES_ANNUAL_TAXES,
}

export const ROUTES_MAP = {
  EXTERNAL_ADMIN_ROUTES,
  ADMIN_ROUTES,
  PAYROLL_ROUTES,
  PUBLIC_ROUTES,
  PRIVATE_ROUTES,
  PRIVATE_ROUTES_ANNUAL_TAXES,
}
